/**
 * ClipTemplatesProvider - Wrapper component that enables VideoTemplatesProvider
 * to work with clip preview context in Content Studio.
 *
 * This component:
 * 1. Uses a SHARED adapter from useClipPreviewRenderer (passed via props)
 * 2. Provides template functionality that updates the visible preview
 */

import { LogManager } from '@src/utility/Logger';
import { observer } from 'mobx-react-lite';
import React, { createContext, useContext, useMemo } from 'react';

import { VideoTemplates } from '../sidepanel/VideoTemplateSettings/VideoTemplate';
import { VideoTemplatesProvider } from '../sidepanel/VideoTemplateSettings/VideoTemplatesContext';

import type { ClipVideoCreatorAdapter } from './hooks/ClipVideoCreatorAdapter';
import type { ClipPreviewEditorStore } from '@src/editor/ClipPreviewEditorStore';
import type { IVideoCreatorStore } from '@src/stores/IVideoCreatorStore';
import type { VideoSourceStore } from '@src/stores-v2/VideoSourceStore';
import type { Story, VideoClip } from '@src/types.ts/story';
import type { ReactNode } from 'react';

const logger = LogManager.getLogger('component:ClipTemplatesProvider');

/**
 * Props for ClipTemplatesProvider
 */
interface ClipTemplatesProviderProps {
  clip: VideoClip;
  story?: Story;
  children?: ReactNode;
  /** Optional: render custom content instead of default VideoTemplates */
  renderContent?: () => ReactNode;
  /** Shared editor store from useClipPreviewRenderer */
  editorStore: ClipPreviewEditorStore | null;
  /** Shared video source store from useClipPreviewRenderer */
  videoSourceStore: VideoSourceStore | null;
  /** Whether the preview is loading */
  isPreviewLoading: boolean;
  /** Shared adapter from useClipPreviewRenderer - required */
  adapter: ClipVideoCreatorAdapter | null;
}

/**
 * Context for accessing clip-specific template state
 */
interface ClipTemplatesContextType {
  adapter: ClipVideoCreatorAdapter | null;
  editorStore: ClipPreviewEditorStore | null;
  videoSourceStore: VideoSourceStore | null;
  isReady: boolean;
}

const ClipTemplatesContext = createContext<ClipTemplatesContextType>({
  adapter: null,
  editorStore: null,
  videoSourceStore: null,
  isReady: false,
});

export const useClipTemplatesContext = () => useContext(ClipTemplatesContext);

/**
 * ClipTemplatesProvider component
 *
 * Uses a SHARED adapter (from props) to ensure template changes
 * are visible in the clip preview and saved correctly on publish.
 */
export const ClipTemplatesProvider: React.FC<ClipTemplatesProviderProps> =
  observer(
    ({
      clip,
      children,
      renderContent,
      editorStore,
      videoSourceStore,
      adapter,
      isPreviewLoading,
    }) => {
      const isReady = !!adapter && !!editorStore;

      // Context value
      const contextValue = useMemo<ClipTemplatesContextType>(
        () => ({
          adapter,
          editorStore,
          videoSourceStore,
          isReady,
        }),
        [isReady, editorStore, videoSourceStore, adapter],
      );

      // Render loading state (don't block on preview loading - show settings immediately)
      if (!isReady || !adapter || !editorStore) {
        return (
          <LoadingContainer>
            <LoadingText>Loading templates...</LoadingText>
          </LoadingContainer>
        );
      }

      logger.debug('ClipTemplatesProvider ready', { clipId: clip.id });

      // Render with VideoTemplatesProvider
      return (
        <ClipTemplatesContext.Provider value={contextValue}>
          <VideoTemplatesProvider
            videoCreator={adapter as unknown as IVideoCreatorStore}
            editorStore={editorStore}
            isClipMode={true}
          >
            {renderContent
              ? renderContent()
              : children || (
                  <VideoTemplates
                    disabled={isPreviewLoading}
                    actionSource="content_studio_popup"
                    clipId={clip.id}
                    clipTitle={clip.title}
                  />
                )}
          </VideoTemplatesProvider>
        </ClipTemplatesContext.Provider>
      );
    },
  );

// Styled components
const LoadingContainer: React.FC<{ children: ReactNode }> = ({ children }) => (
  <div
    style={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '40px',
      color: 'var(--text-secondary)',
    }}
  >
    {children}
  </div>
);

const LoadingText: React.FC<{ children: ReactNode }> = ({ children }) => (
  <span
    style={{
      fontSize: '14px',
      fontFamily: 'Inter, sans-serif',
    }}
  >
    {children}
  </span>
);

export default ClipTemplatesProvider;
